import React, { Suspense } from "react";
import { AssetCreationProvider, useAssetCreation } from "./AssetCreationContext";
import { AnimatePresence, motion } from "framer-motion";
import WizardLayout from "./components/WizardLayout";

// Lazy load steps from index
const Step1Identity = React.lazy(() => import("./steps").then(m => ({ default: m.Step1Identity })));
const Step2Lifecycle = React.lazy(() => import("./steps").then(m => ({ default: m.Step2Lifecycle })));
const Step3Classification = React.lazy(() => import("./steps").then(m => ({ default: m.Step3Classification })));
const Step4Criticality = React.lazy(() => import("./steps").then(m => ({ default: m.Step4Criticality })));
const Step5DataPrivacy = React.lazy(() => import("./steps").then(m => ({ default: m.Step5DataPrivacy })));
const Step6Access = React.lazy(() => import("./steps").then(m => ({ default: m.Step6Access })));
const Step7Security = React.lazy(() => import("./steps").then(m => ({ default: m.Step7Security })));
const Step8Governance = React.lazy(() => import("./steps").then(m => ({ default: m.Step8Governance })));

const Step3And4 = () => (
    <div className="space-y-12">
        <Step3Classification />
        <div className="border-t border-gray-100 pt-12">
            <Step4Criticality />
        </div>
    </div>
);

const StepRenderer = () => {
    const { currentStep, direction } = useAssetCreation();

    // Animation variants
    const variants = {
        enter: (direction: string) => ({
            x: direction === "forward" ? 40 : -40,
            opacity: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
        },
        exit: (direction: string) => ({
            x: direction === "forward" ? -40 : 40,
            opacity: 0,
        }),
    };

    let StepComponent;
    switch (currentStep) {
        case 1: StepComponent = Step1Identity; break;
        case 2: StepComponent = Step2Lifecycle; break;
        case 3: StepComponent = Step3And4; break;
        case 4: StepComponent = Step5DataPrivacy; break;
        case 5: StepComponent = Step6Access; break;
        case 6: StepComponent = Step7Security; break;
        case 7: StepComponent = Step8Governance; break;
        default: StepComponent = () => <div>Unknown Step</div>;
    }

    return (
        <AnimatePresence custom={direction} mode="wait">
            <motion.div
                key={currentStep}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="w-full"
            >
                <Suspense fallback={
                    <div className="flex flex-col items-center justify-center p-20 gap-4">
                        <div className="w-12 h-12 border-4 border-blue-600/10 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Loading Section...</p>
                    </div>
                }>
                    <StepComponent />
                </Suspense>
            </motion.div>
        </AnimatePresence>
    );
};

const CreateAssetPage: React.FC = () => {
    return (
        <AssetCreationProvider>
            <CreateAssetContent />
        </AssetCreationProvider>
    );
};

const CreateAssetContent = () => {
    const { isLoading } = useAssetCreation();

    return (
        <WizardLayout>
            <div className="relative">
                {isLoading && (
                    <div className="absolute inset-0 bg-white/60 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-4 rounded-3xl">
                        <div className="w-16 h-16 border-4 border-blue-600/10 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-lg font-bold text-gray-900">Loading your draft...</p>
                        <p className="text-sm text-gray-500">Just a moment while we get everything ready.</p>
                    </div>
                )}
                <StepRenderer />
            </div>
        </WizardLayout>
    );
}

export default CreateAssetPage;
